import { proxy } from 'valtio';
import { ApiService } from '@/services/api';

// 发言记录类型
export interface TranscriptionItem {
  id: string;
  speaker: string;
  startTime: string;
  content: string;
}

// Store 只负责数据，保持简单
export const TranscriptionStore = proxy({
  items: [] as TranscriptionItem[],
  idCounter: 1,
});

// --- 合并状态（不需要响应式，不放进 proxy） ---
let isMerging = false;
let lastMergedItemId: string | null = null;

/**
 * 添加一条转录记录并尝试触发合并
 */
export function addTranscriptionItem(speaker: string, startTime: string, content: string) {
  const newItem: TranscriptionItem = {
    id: `${TranscriptionStore.idCounter++}`,
    speaker,
    startTime,
    content,
  };
  TranscriptionStore.items.push(newItem);

  if (!isMerging) {
    tryMerge();
  }
}

/** 重置所有状态 */
export function resetTranscriptions() {
  TranscriptionStore.items = [];
  TranscriptionStore.idCounter = 1;
  isMerging = false;
  lastMergedItemId = null;
}

/**
 * 尝试发起合并
 * 范围：从 lastMergedItemId 对应的 item 到最新 item
 */
async function tryMerge() {
  const items = TranscriptionStore.items;

  // 用 id 查找起始位置，避免 index 漂移
  const startIndex = lastMergedItemId
    ? items.findIndex(item => item.id === lastMergedItemId)
    : 0;
  const safeStart = startIndex === -1 ? 0 : startIndex;

  // 不足 2 个不合并
  if (items.length - safeStart < 2) return;

  isMerging = true;
  const mergeSlice = items.slice(safeStart);
  console.log('[tryMerge] 发起合并, 范围:', { safeStart, count: mergeSlice.length, ids: mergeSlice.map(i => i.id) });

  try {
    const response = await ApiService.mergeTranscriptions(mergeSlice);
    const result = response.data;
    console.log('[tryMerge] 合并接口返回:', JSON.stringify(result, null, 2));

    if (result.success && result.data?.mergedItems) {
      const mergedIds = new Set(mergeSlice.map(item => item.id));
      const kept = TranscriptionStore.items.filter(item => !mergedIds.has(item.id));
      const all = [...result.data.mergedItems, ...kept];
      all.sort((a, b) => Number(a.id) - Number(b.id));
      TranscriptionStore.items = all;
    }

    const currentItems = TranscriptionStore.items;
    console.log('[tryMerge] 合并后 items:', currentItems.map(i => ({ id: i.id, content: i.content })));
    if (currentItems.length > 0) {
      lastMergedItemId = currentItems[currentItems.length - 1].id;
    }
  } catch (err) {
    console.error('转录合并失败:', err);
    if (items.length > 0) {
      lastMergedItemId = items[items.length - 1].id;
    }
  } finally {
    isMerging = false;
    // 合并期间有新 item，再跑一轮
    if (hasNewItemsSince(lastMergedItemId)) {
      tryMerge();
    }
  }
}

/** 检查是否有足够的新 item 需要合并 */
function hasNewItemsSince(lastId: string | null): boolean {
  if (!lastId) return TranscriptionStore.items.length >= 2;
  const items = TranscriptionStore.items;
  const idx = items.findIndex(item => item.id === lastId);
  return idx !== -1 && items.length - idx >= 2;
}
